import {
  WidgetSlot,
  WidgetPointer,
} from '@wix/editor-platform-host-integration-apis';
import { CompRef, DocumentServicesObject } from '@wix/document-services-types';

import { PlatformContext } from '../../../../../types/platformApi';
import { SlotPointer } from '../../slotPointer';

import {
  installPluginApp,
  isAppProvisioned,
  reportPluginBi,
  serializePlacement,
} from '../../utils';

import {
  addPluginTry,
  pluginAdded,
  pluginRemoved,
} from '@wix/bi-logger-editor-platform-data/v2';
import { IExperimentsApiAdapter } from '../../../../utils/experimentsApiAdapter';
import { platformEvents } from '../../../../../platformEvents';
import {
  PluginHostApiImplementation,
  PluginHostApi,
  PluginActionFullContext,
} from '../../types';
import { getEditorSubType } from '../../../../../utils';

export type PluginManagerType = 'blocksAppEditor' | 'editor' | 'sdk';

export interface PluginManagerImplementationAPIs {
  actionContext: PluginActionFullContext;
  platformContext: PlatformContext;
  hostApiMethods: PluginHostApi;
  documentServicesAPI: DocumentServicesObject;
}
export interface PluginAddParams {
  slotPointer: SlotPointer;
  widgetPointer: WidgetPointer;
}

export interface PluginRemoveParams {
  slotPointer: SlotPointer;
}

export function getAddPluginSteps(
  params: PluginAddParams,
  apis: PluginManagerImplementationAPIs,
  managerType: PluginManagerType,
): {
  getPlacement: () => Promise<WidgetSlot['placement']>;
  biReportAddTry: (placement: WidgetSlot['placement']) => Promise<void>;
  biReportAddSuccess: (placement: WidgetSlot['placement']) => Promise<void>;
  installPluginAppIfNeeded: () => Promise<void>;
  populate: () => Promise<CompRef>;
  postInstallWait: () => Promise<void>;
  notifyHostApp: (placement: WidgetSlot['placement']) => Promise<void>;
} {
  const {
    hostApiMethods,
    actionContext,
    platformContext,
    documentServicesAPI,
  } = apis;
  const { slotPointer, widgetPointer } = params;
  return {
    async getPlacement() {
      const {
        getPlacementInBlocksAppEditor,
        getWidgetSlotPlacement,
        getSlotIdInBlocksAppEditor,
      } = hostApiMethods;
      let placement: WidgetSlot['placement'] | undefined;
      switch (managerType) {
        case 'blocksAppEditor': {
          const slotKeyName = getSlotIdInBlocksAppEditor(
            slotPointer.toBlocks().slotRef,
          );
          placement = getPlacementInBlocksAppEditor(slotKeyName);
          break;
        }
        case 'editor':
          placement = getWidgetSlotPlacement(slotPointer, {
            includePrivate: false,
          });
          break;
        case 'sdk': {
          placement = getWidgetSlotPlacement(slotPointer, {
            includePrivate: true,
          });
          break;
        }
      }

      if (!placement) {
        throw new Error(
          `Plugins Host API: 'installWidgetPlugin' missing placement params`,
        );
      }
      return placement;
    },
    async biReportAddTry(placement: WidgetSlot['placement']): Promise<void> {
      const userRole = managerType === 'blocksAppEditor' ? 'dev' : 'user';
      reportPluginBi(
        addPluginTry({
          origin: actionContext.biOrigin,
          target_type: 'widget_plugins',
          role: userRole,
          editorType: getEditorSubType(),
          ...platformContext.intentInstallServices.getBIExtras({
            appDefinitionId: widgetPointer.appDefinitionId,
          }),
          slot_id: slotPointer.getSlotBiId(),
          plugin_id: widgetPointer.widgetId,
          host_app_id: placement.appDefinitionId,
          placement_id: serializePlacement(placement),
          app_def_id_plugin: widgetPointer.appDefinitionId,
        }),
      );
    },
    async biReportAddSuccess(
      placement: WidgetSlot['placement'],
    ): Promise<void> {
      const userRole = managerType === 'blocksAppEditor' ? 'dev' : 'user';
      reportPluginBi(
        pluginAdded({
          origin: actionContext.biOrigin,
          target_type: 'widget_plugins',
          role: userRole,
          editorType: getEditorSubType(),
          ...platformContext.intentInstallServices.getBIExtras({
            appDefinitionId: widgetPointer.appDefinitionId,
          }),
          slot_id: slotPointer.getSlotBiId(),
          plugin_id: widgetPointer.widgetId,
          host_app_id: placement.appDefinitionId,
          placement_id: serializePlacement(placement),
          app_def_id_plugin: widgetPointer.appDefinitionId,
        }),
      );
    },
    async installPluginAppIfNeeded() {
      const provisioned = isAppProvisioned(
        documentServicesAPI,
        widgetPointer.appDefinitionId,
      );

      if (!provisioned) {
        await installPluginApp(
          documentServicesAPI,
          widgetPointer,
          actionContext,
        );
      }
    },
    async populate() {
      const { resolveHostAPIBySlotPointer } = hostApiMethods;
      const compRef = await resolveHostAPIBySlotPointer(
        slotPointer,
      ).populateWidgetPluginToSlot(slotPointer, widgetPointer);
      return compRef;
    },
    async postInstallWait() {
      await documentServicesAPI.waitForChangesAppliedAsync();
    },
    async notifyHostApp(placement: WidgetSlot['placement']) {
      const { resolveHostAPIBySlotPointer } = hostApiMethods;
      const { applicationId } = documentServicesAPI.tpa.app.getDataByAppDefId(
        placement.appDefinitionId!,
      );
      const hostComponentRef =
        resolveHostAPIBySlotPointer(
          slotPointer,
        ).getWidgetHostRefBySlotContainer(slotPointer);

      documentServicesAPI.platform.notifyApplication(
        applicationId!,
        platformEvents.factory.widgetPluginAdded({
          slotComponentRef: slotPointer.getPlatformEventSlotRef(),
          hostComponentRef,
          placement: {
            appDefinitionId: placement.appDefinitionId!,
            widgetId: placement.widgetId!,
            slotId: placement.slotId!,
          },
          plugin: widgetPointer,
        }),
      );
    },
  };
}

export function getRemovePluginSteps(
  params: PluginRemoveParams,
  apis: PluginManagerImplementationAPIs,
  managerType: PluginManagerType,
): {
  getPlacement: () => WidgetSlot['placement'];
  getWidgetPointer: () => WidgetPointer | undefined;
  removeFromSlot: () => void;
  biReportRemoveSuccess: (
    placement: WidgetSlot['placement'],
    widgetPointer: WidgetPointer,
  ) => void;
  notifyHostApp: (
    placement: WidgetSlot['placement'],
    widgetPointer: WidgetPointer,
  ) => Promise<void>;
} {
  const {
    hostApiMethods,
    actionContext,
    platformContext,
    documentServicesAPI,
  } = apis;
  const { resolveHostAPIBySlotPointer } = hostApiMethods;
  const { slotPointer } = params;
  // Resolve the widget pointer up front: once removeFromSlot runs the slot is
  // empty and getWidgetPointer can no longer recover it.
  const slotWidgetPointer = hostApiMethods.getWidgetPointer(slotPointer);
  return {
    getPlacement() {
      return hostApiMethods.getWidgetSlotPlacement(slotPointer, {
        includePrivate: managerType === 'sdk',
      });
    },
    getWidgetPointer() {
      return slotWidgetPointer;
    },
    async removeFromSlot() {
      resolveHostAPIBySlotPointer(slotPointer).uninstallWidgetPlugin(
        slotPointer,
      );
    },
    async biReportRemoveSuccess(
      placement: WidgetSlot['placement'],
      widgetPointer: WidgetPointer,
    ) {
      const userRole = managerType === 'blocksAppEditor' ? 'dev' : 'user';
      reportPluginBi(
        pluginRemoved({
          origin: actionContext.biOrigin,
          target_type: 'widget_plugins',
          role: userRole,
          editorType: getEditorSubType(),
          ...platformContext.intentInstallServices.getBIExtras({
            appDefinitionId: widgetPointer.appDefinitionId,
          }),
          slot_id: slotPointer.getSlotBiId(),
          plugin_id: widgetPointer.widgetId,
          host_app_id: placement.appDefinitionId,
          placement_id: serializePlacement(placement),
          app_def_id_plugin: widgetPointer.appDefinitionId,
        }),
      );
    },
    async notifyHostApp(
      placement: WidgetSlot['placement'],
      widgetPointer: WidgetPointer,
    ) {
      const { applicationId } = documentServicesAPI.tpa.app.getDataByAppDefId(
        placement.appDefinitionId!,
      );
      const hostComponentRef =
        resolveHostAPIBySlotPointer(
          slotPointer,
        ).getWidgetHostRefBySlotContainer(slotPointer);

      documentServicesAPI.platform.notifyApplication(
        applicationId!,
        platformEvents.factory.widgetPluginRemoved({
          slotComponentRef: slotPointer.getPlatformEventSlotRef(),
          hostComponentRef,
          placement: {
            appDefinitionId: placement.appDefinitionId!,
            widgetId: placement.widgetId!,
            slotId: placement.slotId!,
          },
          plugin: widgetPointer,
        }),
      );
    },
  };
}
